import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
      continue; // Don't get the children since it will reload the template
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"CI_API_V4_URL":"https://gitlab.lanhuapp.com/api/v4","CI_COMMIT_SHORT_SHA":"dec1fd14","CI_PROJECT_NAME":"frontend-universe","MGAGENT_PRE_KEY":"7yd46gSxH3e9JERg","CI_COMMIT_REF_PROTECTED":"false","NODE_VERSION":"v16.13.0","projectName":"frontend-universe","npm_package_dependencies_vue_class_component":"^7.2.6","ALIYUN_SECRET_KEY":"4dqqooPZg5Tk67WK7wTG9LJXSxMprh","CI_BUILD_TOKEN":"yC4J2yHLQ43smtxmh8tn","HOSTNAME":"runner-j-ghg7kd-project-1141-concurrent-048mxz","npm_package_dependencies_v_scroll_lock":"^1.3.1","npm_package_dependencies__tiptap_extension_link":"^2.0.0-beta.36","NODE":"/opt/node-v16.13.0-linux-x64/bin/node","CI_PROJECT_URL":"https://gitlab.lanhuapp.com/master/frontend/frontend-universe","npm_package_dependencies__tiptap_vue_2":"^2.0.0-beta.77","npm_package_dependencies__tiptap_extension_character_count":"^2.0.0-beta.25","npm_package_dependencies__nuxtjs_dayjs":"^1.4.1","INIT_CWD":"/builds/master/frontend/frontend-universe","CI_PROJECT_VISIBILITY":"private","npm_package_dependencies_axios":"^0.25.0","npm_config_version_git_tag":"true","FF_NETWORK_PER_BUILD":"false","CI_JOB_URL":"https://gitlab.lanhuapp.com/master/frontend/frontend-universe/-/jobs/1172637","KUBERNETES_PORT":"tcp://10.140.0.1:443","KUBERNETES_PORT_443_TCP_PORT":"443","npm_package_dependencies_eventemitter3":"^4.0.7","npm_package_dependencies__sentry_tracing":"^6.17.3","npm_package_dependencies__master_mg_tokens":"^0.0.17","npm_config_cwebp_binary_site":"https://npmmirror.com/mirrors/cwebp-bin","npm_package_dependencies_babel_plugin_component":"^1.1.1","npm_config_sentrycli_cdnurl":"https://npmmirror.com/mirrors/sentry-cli","CI_REGISTRY_USER":"gitlab-ci-token","npm_package_dependencies__nuxtjs_axios":"^5.13.6","npm_package_scripts_lint":"cross-env LINT=1 eslint --ext .js,.vue .","npm_config_init_license":"MIT","PROD_TOOLS_ES_PASSWORD":"Z127cbFYW21M","CI_BUILD_BEFORE_SHA":"0000000000000000000000000000000000000000","FF_USE_LEGACY_KUBERNETES_EXECUTION_STRATEGY":"true","KUBERNETES_SERVICE_PORT":"443","npm_package_dependencies_qrcode_vue":"1","npm_config_chromedriver_cdnurl":"https://npmmirror.com/mirrors/chromedriver","STATIC_BUCKET":"oss-cn-beijing-internal.aliyuncs.com","CI_SERVER_VERSION":"12.2.5","GITLAB_USER_LOGIN":"zengcheng","CI_BUILD_ID":"1172637","npm_package_devDependencies__nuxt_typescript_build":"^2.1.0","npm_package_scripts_dev":"cross-env CURRENT_ENV=dev nuxt --dotenv .env.dev","GITLAB_USER_EMAIL":"zengcheng@jwzg.com","npm_package_devDependencies_sass_loader":"10","npm_package_dependencies_nuxt_winston_log":"^1.2.0","KUBERNETES_SERVICE_HOST":"10.140.0.1","npm_config_yarn_offline_mirror_pruning":"true","npm_package_private":"true","npm_config_registry":"https://nexus.mastergo.com/repository/npm-group/","CI_COMMIT_TITLE":"Merge branch 'feat_upgrade_omni_v5.4.13' into 'master'","CI_DISPOSABLE_ENVIRONMENT":"true","CI_RUNNER_EXECUTABLE_ARCH":"linux/amd64","LC_ALL":"en_US.UTF-8","npm_package_dependencies__tiptap_starter_kit":"^2.0.0-beta.183","npm_package_scripts_generate":"nuxt generate","SONAR_USERNAME":"admin","FF_USE_GO_CLOUD_WITH_CACHE_ARCHIVER":"true","CI_SERVER_VERSION_PATCH":"5","CI_COMMIT_REF_NAME":"v2.15.1","CI_SERVER_TLS_CA_FILE":"/builds/master/frontend/frontend-universe.tmp/CI_SERVER_TLS_CA_FILE","npm_package_readmeFilename":"README.md","npm_package_devDependencies_svg_sprite_loader":"^6.0.11","CI_SERVER_VERSION_MINOR":"2","npm_package_description":"## 项目启动","npm_package_devDependencies_webpack":"^4.46.0","HARBOR_PASSWORD":"MasterPc2A41tZ3","CI_JOB_TOKEN":"yC4J2yHLQ43smtxmh8tn","FF_SHELL_EXECUTOR_USE_LEGACY_PROCESS_KILL":"false","CI_PROJECT_ID":"1141","CI_JOB_IMAGE":"harbor.mastergo.com/master-tools/basenpm:v16.13.0","npm_package_dependencies_vue_clipboard2":"^0.3.3","npm_config_phantomjs_cdnurl":"https://npmmirror.com/mirrors/phantomjs","HARBOR_HOST":"harbor.mastergo.com","CI_RUNNER_ID":"705","CI_RUNNER_REVISION":"8fa89735","npm_package_dependencies_vue_property_decorator":"^9.1.2","npm_package_dependencies_lru_cache":"^7.4.0","YARN_CACHE_FOLDER":"/opt/cache/yarn","npm_package_dependencies__nuxtjs_sentry":"^5.1.7","npm_package_pre_commit":"lint","npm_config_canvas_binary_host_mirror":"https://npmmirror.com/mirrors/node-canvas-prebuilt/","CI_SERVER_VERSION_MAJOR":"12","npm_package_dependencies_xgplayer":"^2.31.4","GITLAB_API_TOKEN":"KAYvV-Hzz6HPmisJs_xz","npm_package_dependencies_lodash":"^4.17.21","CI_COMMIT_DESCRIPTION":"\nfeat: update @master/mg-omni to 5.4.13\n\nSee merge request master/frontend/frontend-universe!488","npm_package_devDependencies_eslint":"^8.11.0","npm_package_dependencies_ali_oss":"^6.17.1","npm_package_dependencies__master_master_sensors":"^0.1.3","npm_package_scripts_dev_native":"cross-env CURRENT_ENV=native nuxt","CI_COMMIT_MESSAGE":"Merge branch 'feat_upgrade_omni_v5.4.13' into 'master'\n\nfeat: update @master/mg-omni to 5.4.13\n\nSee merge request master/frontend/frontend-universe!488","npm_execpath":"/opt/node-v16.13.0-linux-x64/lib/node_modules/yarn/bin/yarn.js","npm_package_devDependencies_babel_plugin_transform_remove_console":"^6.9.4","npm_package_devDependencies__typescript_eslint_eslint_plugin":"^5.15.0","AWS_ECR_HOST":"544142272484.dkr.ecr.ap-southeast-1.amazonaws.com","npm_package_dependencies__sentry_webpack_plugin":"^1.19.0","MGAGENT_DEV_KEY":"YyVQCVvfRussHAAy","PROD_TOOLS_ES_HOST":"es-cn-nif1wc9fr00179kpy.elasticsearch.aliyuncs.com","SONAR_PASSWORD":"TnsZadm20cZ","CI_BUILD_REF_NAME":"v2.15.1","CI_PIPELINE_ID":"446594","CI_BUILD_REF":"dec1fd142ad2b5df7f0df876e3cf246e7b1cecb3","CI_BUILD_TAG":"v2.15.1","CI_COMMIT_REF_SLUG":"v2-15-1","CI_PAGES_DOMAIN":"gitlab.lanhuapp.com","npm_package_scripts_local_build_native":"cross-env CURRENT_CLIENT_ENV=native MODE_CLIENT_ENV=CLIENT nuxt build && cross-env CURRENT_ENV=native MODE_ENV=SERVER nuxt start","npm_package_scripts_dev_host":"cross-env CURRENT_ENV=dev nuxt --hostname 0.0.0.0","HARBOR_USERNAME":"master","NPM_TOKEN":"NpmToken.38664ab1-82f2-30d3-96f8-068806a67900","CI_REPOSITORY_URL":"https://gitlab-ci-token:yC4J2yHLQ43smtxmh8tn@gitlab.lanhuapp.com/master/frontend/frontend-universe.git","PATH":"/tmp/yarn--1769156282591-0.5970271666763411:/builds/master/frontend/frontend-universe/node_modules/.bin:/usr/local/share/.config/yarn/link/node_modules/.bin:/opt/node-v16.13.0-linux-x64/libexec/lib/node_modules/npm/bin/node-gyp-bin:/opt/node-v16.13.0-linux-x64/lib/node_modules/npm/bin/node-gyp-bin:/opt/node-v16.13.0-linux-x64/bin/node_modules/npm/bin/node-gyp-bin:/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin:/opt/node/bin","GITLAB_USER_NAME":"曾诚","npm_config_argv":"{\"remain\":[],\"cooked\":[\"run\",\"build\"],\"original\":[\"build\"]}","npm_package_dependencies_swiper":"5.4.5","npm_package_dependencies__master_mg_menu":"0.0.27","CURRENT_CLIENT_VERSION":"v2.15.1","CI_REGISTRY_PASSWORD":"yC4J2yHLQ43smtxmh8tn","CI_BUILD_STAGE":"build","FF_RESET_HELPER_IMAGE_ENTRYPOINT":"true","CI_PROJECT_DIR":"/builds/master/frontend/frontend-universe","CI_RUNNER_TAGS":"k8s-runner-mastergo","FF_USE_DIRECT_DOWNLOAD":"true","_":"/builds/master/frontend/frontend-universe/node_modules/.bin/nuxt","npm_package_dependencies_vue":"^2.6.14","AWS_DEFAULT_REGION":"ap-southeast-1","CI_NODE_TOTAL":"1","npm_package_dependencies_async_validator":"^4.0.7","PWD":"/builds/master/frontend/frontend-universe","npm_package_dependencies_core_js":"^3.19.3","AWS_SECRET_ACCESS_KEY":"UsltTAxErP69Es382DPp8akK8AyHlDRBJt6k1nm3","CI_PAGES_URL":"http://master.gitlab.lanhuapp.com/frontend/frontend-universe","CI_JOB_STAGE":"build","CI_PIPELINE_SOURCE":"push","CI_SERVER_HOST":"gitlab.lanhuapp.com","npm_lifecycle_event":"build","CI_SERVER_NAME":"GitLab","CI_PROJECT_PATH":"master/frontend/frontend-universe","LANG":"en_US.UTF-8","npm_package_name":"mg_universe","MGAGENT_PROD_KEY":"TnmlpIYB2xjL4M1G","CI_COMMIT_BEFORE_SHA":"0000000000000000000000000000000000000000","GITLAB_FEATURES":"","npm_package_devDependencies_eslint_plugin_import":"^2.25.4","CI_PIPELINE_IID":"2222","NODE_PATH":"/opt/node/lib/node_modules","npm_package_devDependencies_sass":"^1.89.2","npm_package_dependencies_vue_awesome_swiper":"^4.1.1","npm_package_scripts_start":"nuxt start","npm_package_scripts_build":"nuxt build --dotenv .env.prod","npm_config_version_commit_hooks":"true","CI_PIPELINE_URL":"https://gitlab.lanhuapp.com/master/frontend/frontend-universe/pipelines/446594","GITLAB_CI":"true","CI_RUNNER_VERSION":"13.6.0","npm_package_dependencies_crypto_js":"^4.1.1","npm_config_bin_links":"true","npm_package_devDependencies_pre_commit":"^1.2.2","npm_config_electron_mirror":"https://npmmirror.com/mirrors/electron/","SUB_PRIVATE_KEY":"/builds/master/frontend/frontend-universe.tmp/SUB_PRIVATE_KEY","CI_SERVER_REVISION":"09f8edbc29a","npm_package_devDependencies_eslint_config_prettier":"^8.5.0","CI_COMMIT_SHA":"dec1fd142ad2b5df7f0df876e3cf246e7b1cecb3","npm_package_devDependencies_eslint_config_airbnb_base":"^15.0.0","npm_package_version":"1.0.1","npm_config_sass_binary_site":"https://npmmirror.com/mirrors/node-sass/","npm_config_sqlite3_binary_host_mirror":"https://foxgis.oss-cn-shanghai.aliyuncs.com/","PROD_TOOLS_ES_USER":"elastic","AWS_ACCESS_KEY_ID":"AKIAX5MLIE7SP3OLO4GD","CI_BUILDS_DIR":"/builds","SPIN_CONFIG":"/builds/master/frontend/frontend-universe.tmp/SPIN_CONFIG","CI_BUILD_NAME":"prodBuild","FF_CMD_DISABLE_DELAYED_ERROR_LEVEL_EXPANSION":"false","CI_CONFIG_PATH":".gitlab-ci.yml","HOME":"/root","SHLVL":"2","CI_PROJECT_PATH_SLUG":"master-frontend-frontend-universe","CI_SERVER":"yes","FF_SKIP_NOOP_BUILD_STAGES":"true","npm_package_devDependencies_eslint_plugin_vue":"^8.5.0","npm_config_cache_folder":"/opt/cache/yarn","KUBERNETES_PORT_443_TCP_PROTO":"tcp","npm_config_save_prefix":"^","npm_config_strict_ssl":"true","PROD_TOOLS_ES_PORT":"9200","CI_RUNNER_SHORT_TOKEN":"J-ghG7Kd","CI":"true","KUBERNETES_SERVICE_PORT_HTTPS":"443","npm_package_devDependencies_cross_env":"^7.0.3","npm_config_version_git_message":"v%s","MODE_CLIENT_ENV":"CLIENT","CI_JOB_STATUS":"running","npm_package_dependencies_xss":"^1.0.11","npm_package_dependencies_babel_eslint":"^10.1.0","npm_config_yarn_offline_mirror":"/opt/cache/npm-packages-offline-cache-frontend-universe","CI_PROJECT_NAMESPACE":"master/frontend","npm_package_devDependencies__nuxtjs_dotenv":"^1.4.1","CURRENT_CLIENT_ENV":"prod","ALIYUN_ACCESS_ID":"LTAI4Fy3PdRGhTUxmgpiRaqu","YARN_WRAP_OUTPUT":"false","npm_package_dependencies_vue_server_renderer":"^2.6.14","npm_package_dependencies__master_mg_omni":"5.4.13","CI_CONCURRENT_ID":"0","npm_lifecycle_script":"nuxt build --dotenv .env.prod","npm_package_dependencies_element_ui":"^2.15.6","imageVersion":"dec1fd14","GIT_DEPTH":"5","npm_package_dependencies_vuex_persistedstate":"^4.1.0","CI_BUILD_REF_SLUG":"v2-15-1","npm_package_devDependencies_nuxt":"^2.15.8","npm_config_version_git_sign":"","npm_config_ignore_scripts":"","npm_config_user_agent":"yarn/1.22.18 npm/? node/v16.13.0 linux x64","CDN_PATH":"https://static.mastergo.com/universe","CI_RUNNER_DESCRIPTION":"gitlab-runner-mastergo-gitlab-runner-85d5c9c5b4-5ltzh","FF_USE_FASTZIP":"false","KUBERNETES_PORT_443_TCP_ADDR":"10.140.0.1","npm_package_devDependencies__nuxt_types":"^2.15.8","GITLAB_USER_ID":"855","CI_JOB_ID":"1172637","KUBERNETES_PORT_443_TCP":"tcp://10.140.0.1:443","npm_package_dependencies_vue_template_compiler":"^2.6.14","npm_package_dependencies__master_master_icons":"0.2.89","npm_config_init_version":"1.0.0","npm_config_ignore_optional":"","CI_COMMIT_TAG":"v2.15.1","npm_config_profiler_binary_host_mirror":"https://npmmirror.com/mirrors/node-inspector/","SONAR_HOST":"https://sonar.mastergo.com","CI_JOB_NAME":"prodBuild","CI_CONCURRENT_PROJECT_ID":"0","NODE_HOME":"/opt/node","npm_package_devDependencies_eslint_plugin_vuejs_accessibility":"^1.1.1","npm_node_execpath":"/opt/node-v16.13.0-linux-x64/bin/node","npm_config_version_tag_prefix":"v","NODE_ENV":"production","_applied":"true","NUXT_ENV_AXIOS_BASE_URL":"http://127.0.0.1:3000","CURRENT_ENV":"prod"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
          window.location.replace(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
